/**
 * @module engine3d/models/conveying
 * @summary 3D models for the Feeders & Conveyors and Pneumatic Conveying
 *   families.
 * @description
 *   The two families share a vocabulary — a barrel with a screw in it, a hopper
 *   over a drive, a cone under a filter — so the archetypes are built once here
 *   and each symbol parameterises one. Anything that moves material gets a
 *   visible drive (motor + gearbox); that is the cue that reads as "machine"
 *   rather than "vessel" from across the room.
 */
import {
  TAU,
  beam,
  box,
  filletPath,
  tube,
  boxCentered,
  cylinder,
  cylinderX,
  cylinderZ,
  flange,
  frustum,
  gearbox,
  ladder,
  merge,
  motorUnit,
  nozzle,
  p,
  pm,
  rectFoot,
  revolve,
  ringLegs,
  rollerBank,
  rotateY,
  rotateZ,
  roundFoot,
  screwFlight,
  skid,
  translate,
  v3,
  type Builder,
  type Mesh,
  type Model3,
} from './kit';

/** Drive train (gearbox + motor) hung off the -X end of a machine. */
function endDrive(x: number, y: number, size: number): { cast: Mesh; motor: Mesh } {
  const mu = motorUnit(size * 1.5, size * 0.42);
  return {
    cast: translate(gearbox(size * 0.8, size * 1.5, size * 1.2), x, y - size * 0.75, 0),
    motor: translate(merge(mu.body, mu.fins), x - size * 1.1, y, 0),
  };
}

/** A feed / discharge spout: a short rectangular chute with a flange. */
function spout(x: number, y: number, w: number, h: number, dep: number, up: boolean): Mesh {
  return merge(
    translate(box(w, h, dep), x, up ? y : y - h, 0),
    translate(box(w * 1.2, 1.4, dep * 1.2), x, up ? y + h : y - h, 0),
  );
}

// ─── Screw / belt conveying ──────────────────────────────────────────────

/** Screw conveyor — barrel along +X, drive at one end, spouts at both. */
export const screwconv: Builder = ({ w, h, d }): Model3 => {
  const R = Math.min(h, d) / 2.4;
  const drop = h * 0.3;
  // High enough that the discharge spout finishes above the floor. Sized from
  // h alone the spout hung under the legs, and since `groundModel` lifts a model
  // until its lowest point rests on the floor, the conveyor came out standing on
  // its own spout with the legs dangling.
  const cy = R + drop + 2;
  const len = w * 0.76;
  return {
    parts: [
      p(
        merge(
          translate(cylinderX(R, len, 18), 0, cy, 0),
          translate(cylinderX(R * 1.12, 2, 18), len / 2, cy, 0),
          translate(cylinderX(R * 1.12, 2, 18), -len / 2, cy, 0),
        ),
        'stainless',
      ),
      p(translate(screwFlight(R * 0.82, len * 0.9, 5, R * 0.24), 0, cy, 0), 'steel'),
      p(spout(-len * 0.34, cy + R, R * 1.5, drop, R * 1.6, true), 'stainless'),
      p(spout(len * 0.34, cy - R, R * 1.5, drop, R * 1.6, false), 'stainless'),
      p(endDrive(len / 2 + R * 0.9, cy, R * 1.1).cast, 'cast'),
      p(endDrive(len / 2 + R * 0.9, cy, R * 1.1).motor, 'motor'),
      p(
        merge(
          translate(boxCentered(3, cy - R, 3), -len * 0.36, (cy - R) / 2, 0),
          translate(boxCentered(3, cy - R, 3), len * 0.3, (cy - R) / 2, 0),
        ),
        'galv',
      ),
    ],
    height: cy + R + drop + 2,
    ports: {
      N: { p: v3(-len * 0.34, cy + R + drop + 1, 0), n: v3(0, 1, 0) },
      S: { p: v3(len * 0.34, cy - R - drop - 1, 0), n: v3(0, -1, 0) },
      W: { p: v3(-len / 2 - 1, cy, 0), n: v3(-1, 0, 0) },
      E: { p: v3(len / 2 + R * 2.2, cy, 0), n: v3(1, 0, 0) },
    },
    footprint: rectFoot(w, d),
  };
};

/** Belt conveyor — troughed belt on idlers between head and tail pulleys. */
export const beltconv: Builder = ({ w, h, d }): Model3 => {
  const beltY = h * 0.78;
  const pr = h * 0.3;
  const len = w * 0.9;
  const legH = beltY - pr;
  return {
    parts: [
      pm(
        'galv',
        translate(boxCentered(len, 2.6, 2.6), 0, beltY, d / 2 - 1.6),
        translate(boxCentered(len, 2.6, 2.6), 0, beltY, -(d / 2 - 1.6)),
        translate(boxCentered(3, legH, 3), -len * 0.36, legH / 2, d / 2 - 2),
        translate(boxCentered(3, legH, 3), -len * 0.36, legH / 2, -(d / 2 - 2)),
        translate(boxCentered(3, legH, 3), len * 0.36, legH / 2, d / 2 - 2),
        translate(boxCentered(3, legH, 3), len * 0.36, legH / 2, -(d / 2 - 2)),
        beam(v3(-len * 0.36, legH * 0.4, d / 2 - 2), v3(len * 0.36, legH * 0.1, d / 2 - 2), 1.8),
      ),
      // Belt: top and return runs wrapped round the two pulleys.
      p(
        merge(
          translate(box(len, 1.6, d * 0.78), 0, beltY + pr, 0),
          translate(box(len, 1.6, d * 0.78), 0, beltY - pr - 1.6, 0),
          translate(cylinderZ(pr + 0.8, d * 0.78, 16), len / 2, beltY, 0),
          translate(cylinderZ(pr + 0.8, d * 0.78, 16), -len / 2, beltY, 0),
        ),
        'rubber',
      ),
      p(translate(rollerBank(pr * 0.34, d * 0.8, len * 0.86, 6), 0, beltY - pr * 0.3, 0), 'steel'),
      p(translate(merge(motorUnit(w * 0.2, pr * 0.5).body, motorUnit(w * 0.2, pr * 0.5).fins), len * 0.5, beltY - pr * 1.2, d * 0.62), 'motor'),
      p(translate(gearbox(w * 0.09, pr * 1.3, d * 0.3), len * 0.42, beltY - pr * 1.3, d * 0.5), 'cast'),
      // Side skirts at the loading end.
      p(
        merge(
          translate(box(len * 0.3, pr * 0.9, 1.4), -len * 0.3, beltY + pr, d * 0.39),
          translate(box(len * 0.3, pr * 0.9, 1.4), -len * 0.3, beltY + pr, -d * 0.39),
        ),
        'machine',
      ),
    ],
    height: beltY + pr * 2,
    ports: {
      W: { p: v3(-len / 2 - pr, beltY + pr, 0), n: v3(-1, 0, 0) },
      E: { p: v3(len / 2 + pr, beltY + pr, 0), n: v3(1, 0, 0) },
      N: { p: v3(-len * 0.3, beltY + pr * 2, 0), n: v3(0, 1, 0) },
      S: { p: v3(len * 0.3, beltY - pr, 0), n: v3(0, -1, 0) },
    },
    footprint: rectFoot(w, d),
  };
};

/** Transfer / crusher / basket conveyor variants — an inclined belt. */
function inclinedBelt(rise: number, crusher: boolean, basket: boolean): Builder {
  return ({ w, h, d }): Model3 => {
    const len = w * 0.88;
    const y0 = h * 0.24;
    const y1 = h * (0.24 + rise);
    const pr = h * 0.2;
    const mid = (y0 + y1) / 2;
    const ang = Math.atan2(y1 - y0, len);
    const deck = rotateZ(box(Math.hypot(len, y1 - y0), 2, d * 0.8), ang);
    const guard = rotateZ(box(Math.hypot(len, y1 - y0), pr * 1.2, 1.4), ang);
    return {
      parts: [
        pm(
          'galv',
          translate(boxCentered(3, y0, 3), -len / 2, y0 / 2, d / 2 - 2),
          translate(boxCentered(3, y0, 3), -len / 2, y0 / 2, -(d / 2 - 2)),
          translate(boxCentered(3, y1, 3), len / 2, y1 / 2, d / 2 - 2),
          translate(boxCentered(3, y1, 3), len / 2, y1 / 2, -(d / 2 - 2)),
        ),
        p(translate(deck, 0, mid + pr, 0), basket ? 'stainless' : 'rubber'),
        p(
          merge(
            translate(guard, 0, mid + pr, d * 0.4),
            translate(guard, 0, mid + pr, -d * 0.4),
          ),
          'machine',
        ),
        p(
          merge(
            translate(cylinderZ(pr, d * 0.8, 14), -len / 2, y0 + pr, 0),
            translate(cylinderZ(pr, d * 0.8, 14), len / 2, y1 + pr, 0),
          ),
          'steel',
        ),
        p(translate(merge(motorUnit(w * 0.16, pr * 0.55).body, motorUnit(w * 0.16, pr * 0.55).fins), len * 0.48, y1 - pr * 0.6, d * 0.6), 'motor'),
        ...(crusher
          ? [
              // Crusher head sitting over the discharge.
              p(translate(box(w * 0.2, h * 0.3, d * 0.7), len * 0.3, y1 + pr * 2, 0), 'machineDark'),
              p(translate(cylinderZ(pr * 0.8, d * 0.6, 12), len * 0.3, y1 + pr * 2, 0), 'steel'),
            ]
          : []),
        ...(basket
          ? [p(translate(box(w * 0.5, h * 0.16, d * 0.7), 0, mid + pr * 2.2, 0), 'stainless')]
          : []),
      ],
      height: y1 + pr * (crusher ? 5 : 2.6),
      ports: {
        W: { p: v3(-len / 2 - pr, y0 + pr * 2, 0), n: v3(-1, 0, 0) },
        E: { p: v3(len / 2 + pr, y1 + pr, 0), n: v3(1, 0, 0) },
        N: { p: v3(-len * 0.3, mid + pr * 2, 0), n: v3(0, 1, 0) },
        S: { p: v3(len / 2, y1 - pr, 0), n: v3(0, -1, 0) },
      },
      footprint: rectFoot(w, d),
    };
  };
}

export const transferconv = inclinedBelt(0.42, false, false);
export const crusherconv = inclinedBelt(0.34, true, false);
export const basketconv = inclinedBelt(0.3, false, true);

/** Rotary valve — round rotor housing with square flanges top and bottom. */
export const rotaryvalve: Builder = ({ w, h, d }): Model3 => {
  const R = Math.min(w, d) / 2.1;
  const cy = h * 0.5;
  return {
    parts: [
      p(
        merge(
          translate(cylinderZ(R, d * 0.7, 18), 0, cy, -d * 0.35),
          translate(cylinderZ(R * 1.1, 2, 18), 0, cy, d * 0.35),
          translate(cylinderZ(R * 1.1, 2, 18), 0, cy, -d * 0.37),
        ),
        'cast',
      ),
      // Rotor pockets, glimpsed through the inlet.
      p(
        merge(
          translate(boxCentered(R * 1.7, 1.4, d * 0.6), 0, cy, 0),
          translate(rotateZ(boxCentered(R * 1.7, 1.4, d * 0.6), Math.PI / 2), 0, cy, 0),
        ),
        'steel',
      ),
      pm(
        'machine',
        translate(box(R * 1.9, h * 0.16, R * 1.9), 0, cy + R * 0.85, 0),
        translate(box(R * 1.9, h * 0.16, R * 1.9), 0, cy - R * 0.85 - h * 0.16, 0),
      ),
      p(translate(merge(motorUnit(w * 0.5, R * 0.3).body, motorUnit(w * 0.5, R * 0.3).fins), w * 0.05, cy, d * 0.62), 'motor'),
      p(translate(gearbox(R * 0.7, R * 1.2, d * 0.24), 0, cy - R * 0.6, d * 0.44), 'cast'),
    ],
    height: cy + R * 0.85 + h * 0.16,
    ports: {
      N: { p: v3(0, cy + R * 0.85 + h * 0.16, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, cy - R * 0.85 - h * 0.16, 0), n: v3(0, -1, 0) },
      E: { p: v3(R * 1.1, cy, 0), n: v3(1, 0, 0) },
      W: { p: v3(-R * 1.1, cy, 0), n: v3(-1, 0, 0) },
    },
    footprint: rectFoot(w, d),
  };
};

/**
 * The hopper-over-drive archetype: a conical hopper on a frame with a screw
 * tube out of the bottom. Loss-in-weight, volumetric and side feeders are all
 * this shape with different proportions and fittings.
 */
function hopperFeeder(opts: {
  loadCells?: boolean;
  twin?: boolean;
  agitator?: boolean;
  hopperFrac?: number;
}): Builder {
  return ({ w, h, d }): Model3 => {
    const R = Math.min(w, d) / 2.3;
    const hf = opts.hopperFrac ?? 0.44;
    const frameH = h * (1 - hf) * 0.55;
    const coneH = h * hf * 0.55;
    const bodyH = h * hf * 0.45;
    const hopY = h - hf * h;
    const tubeY = frameH + h * 0.06;
    const tubeLen = w * 0.72;
    return {
      parts: [
        pm(
          'galv',
          translate(boxCentered(3, frameH, 3), w / 2 - 4, frameH / 2, d / 2 - 4),
          translate(boxCentered(3, frameH, 3), -(w / 2 - 4), frameH / 2, d / 2 - 4),
          translate(boxCentered(3, frameH, 3), w / 2 - 4, frameH / 2, -(d / 2 - 4)),
          translate(boxCentered(3, frameH, 3), -(w / 2 - 4), frameH / 2, -(d / 2 - 4)),
          translate(box(w * 0.9, 2.4, d * 0.9), 0, frameH, 0),
        ),
        // Load cells lift the hopper clear of the frame on an LIW unit.
        ...(opts.loadCells
          ? [
              p(
                merge(
                  translate(cylinder(2.6, h * 0.05, { seg: 10 }), w * 0.26, hopY - h * 0.05, d * 0.26),
                  translate(cylinder(2.6, h * 0.05, { seg: 10 }), -w * 0.26, hopY - h * 0.05, d * 0.26),
                  translate(cylinder(2.6, h * 0.05, { seg: 10 }), 0, hopY - h * 0.05, -d * 0.3),
                ),
                'accent',
              ),
            ]
          : []),
        pm(
          'stainless',
          translate(frustum(R * 0.24, R, coneH, { seg: 20, capTop: false, capBottom: false }), 0, hopY, 0),
          translate(cylinder(R, bodyH, { seg: 20, capBottom: false }), 0, hopY + coneH, 0),
          translate(flange(R * 1.07, 1.4, 20), 0, hopY + coneH + bodyH - 1.4, 0),
        ),
        ...(opts.agitator
          ? [p(translate(cylinder(1.4, coneH + bodyH * 0.8, { seg: 8 }), 0, hopY + coneH * 0.2, 0), 'steel')]
          : []),
        // The metering screw(s) under the hopper.
        p(
          opts.twin
            ? merge(
                translate(cylinderX(R * 0.3, tubeLen, 14), 0, tubeY, R * 0.3),
                translate(cylinderX(R * 0.3, tubeLen, 14), 0, tubeY, -R * 0.3),
              )
            : translate(cylinderX(R * 0.36, tubeLen, 14), 0, tubeY, 0),
          'stainless',
        ),
        p(translate(gearbox(w * 0.13, h * 0.14, d * 0.42), w * 0.36, tubeY - h * 0.07, 0), 'cast'),
        p(translate(merge(motorUnit(w * 0.26, R * 0.3).body, motorUnit(w * 0.26, R * 0.3).fins), w * 0.5, tubeY, 0), 'motor'),
        p(translate(box(w * 0.26, h * 0.16, 2.2), -w * 0.28, frameH * 0.4, d / 2), 'panel'),
      ],
      height: hopY + coneH + bodyH,
      ports: {
        N: { p: v3(0, hopY + coneH + bodyH, 0), n: v3(0, 1, 0) },
        W: { p: v3(-tubeLen / 2 - 2, tubeY, 0), n: v3(-1, 0, 0) },
        E: { p: v3(w / 2, tubeY, 0), n: v3(1, 0, 0) },
        S: { p: v3(-tubeLen / 2, tubeY - R * 0.4, 0), n: v3(0, -1, 0) },
      },
      footprint: rectFoot(w, d),
    };
  };
}

export const liwfeeder = hopperFeeder({ loadCells: true, twin: true, agitator: true });
export const volfeeder = hopperFeeder({ hopperFrac: 0.5 });
export const scsfeeder = hopperFeeder({ hopperFrac: 0.36 });
export const tcsfeeder = hopperFeeder({ twin: true, hopperFrac: 0.36 });
export const sidefeeder = hopperFeeder({ twin: true, hopperFrac: 0.4 });
export const forcedfeeder = hopperFeeder({ agitator: true, hopperFrac: 0.55 });
export const debagging = hopperFeeder({ hopperFrac: 0.55 });
export const debaghopper = hopperFeeder({ hopperFrac: 0.6, agitator: true });
export const manualdebag = hopperFeeder({ hopperFrac: 0.42 });

/** Bag dump station — a hopper with a grating and a dust hood at the back. */
export const bagdump: Builder = ({ w, h, d }): Model3 => {
  const legH = h * 0.28;
  const binH = h * 0.34;
  const hoodH = h * 0.38;
  return {
    parts: [
      pm(
        'galv',
        translate(boxCentered(3, legH, 3), w / 2 - 4, legH / 2, d / 2 - 4),
        translate(boxCentered(3, legH, 3), -(w / 2 - 4), legH / 2, d / 2 - 4),
        translate(boxCentered(3, legH, 3), w / 2 - 4, legH / 2, -(d / 2 - 4)),
        translate(boxCentered(3, legH, 3), -(w / 2 - 4), legH / 2, -(d / 2 - 4)),
      ),
      // Hopper bin (square → round) and its outlet.
      p(
        merge(
          translate(rotateY(frustum(Math.min(w, d) * 0.16, Math.min(w, d) * 0.5, binH, { seg: 4, capTop: false, capBottom: false }), Math.PI / 4), 0, legH, 0),
          translate(cylinder(Math.min(w, d) * 0.14, legH * 0.6, { seg: 10 }), 0, legH * 0.3, 0),
        ),
        'stainless',
      ),
      // Dust hood + tipping shelf.
      pm(
        'stainless',
        translate(box(w * 0.94, hoodH, 2), 0, legH + binH, -d * 0.44),
        translate(box(2, hoodH, d * 0.6), -w * 0.46, legH + binH, -d * 0.15),
        translate(box(2, hoodH, d * 0.6), w * 0.46, legH + binH, -d * 0.15),
        translate(box(w * 0.94, 2, d * 0.6), 0, legH + binH + hoodH, -d * 0.15),
        translate(box(w * 0.8, 1.6, d * 0.3), 0, legH + binH, d * 0.3),
      ),
      p(translate(cylinder(Math.min(w, d) * 0.1, 6, { seg: 10 }), w * 0.2, legH + binH + hoodH, -d * 0.2), 'pipe'),
    ],
    height: legH + binH + hoodH + 6,
    ports: {
      N: { p: v3(w * 0.2, legH + binH + hoodH + 6, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, legH * 0.3, 0), n: v3(0, -1, 0) },
      E: { p: v3(w / 2, legH + binH * 0.5, 0), n: v3(1, 0, 0) },
      W: { p: v3(-w / 2, legH + binH * 0.5, 0), n: v3(-1, 0, 0) },
    },
    footprint: rectFoot(w, d),
  };
};

/** Hoist debagging station — a tall gantry with a hoisted bag over a hopper. */
export const hoistdebag: Builder = ({ w, h, d }): Model3 => {
  const base = hopperFeeder({ hopperFrac: 0.3 })({ w, h: h * 0.52, d });
  const gantryH = h;
  const bagR = Math.min(w, d) * 0.24;
  return {
    parts: [
      ...base.parts,
      pm(
        'galv',
        translate(boxCentered(3.4, gantryH, 3.4), w / 2 - 3, gantryH / 2, d / 2 - 3),
        translate(boxCentered(3.4, gantryH, 3.4), -(w / 2 - 3), gantryH / 2, d / 2 - 3),
        translate(boxCentered(3.4, gantryH, 3.4), w / 2 - 3, gantryH / 2, -(d / 2 - 3)),
        translate(boxCentered(3.4, gantryH, 3.4), -(w / 2 - 3), gantryH / 2, -(d / 2 - 3)),
        translate(boxCentered(w - 3, 3, 3), 0, gantryH, d / 2 - 3),
        translate(boxCentered(w - 3, 3, 3), 0, gantryH, -(d / 2 - 3)),
        translate(boxCentered(3, 3, d - 3), 0, gantryH, 0),
      ),
      p(translate(box(w * 0.16, h * 0.06, d * 0.2), 0, gantryH - h * 0.06, 0), 'machine'),
      p(
        merge(
          translate(cylinder(0.9, h * 0.1, { seg: 6 }), 0, gantryH - h * 0.16, 0),
          translate(frustum(bagR, bagR * 0.9, h * 0.26, { seg: 12 }), 0, h * 0.58, 0),
        ),
        'plastic',
      ),
    ],
    height: gantryH,
    ports: base.ports,
    footprint: rectFoot(w, d),
  };
};

/** Vertical feeder silos — the granules / powder / raw-material variants. */
function feederSilo(coneFrac: number, mat: 'steel' | 'stainless'): Builder {
  return ({ w, h, d }): Model3 => {
    const R = Math.min(w, d) / 2;
    const legH = h * 0.18;
    const coneH = h * coneFrac;
    const bodyH = h - legH - coneH;
    return {
      parts: [
        p(ringLegs(R * 0.84, legH, 4, Math.max(1.6, R * 0.1)), 'galv'),
        p(
          revolve(
            [
              { x: R * 0.16, y: legH },
              { x: R, y: legH + coneH },
              { x: R, y: legH + coneH + bodyH * 0.97 },
              { x: R * 0.9, y: h },
              { x: R * 0.24, y: h },
            ],
            22,
            false,
          ),
          mat,
        ),
        pm(
          'stainless',
          translate(cylinder(R * 0.2, legH * 0.55, { seg: 10 }), 0, legH * 0.25, 0),
          translate(flange(R * 0.3, 1.6, 12), 0, h, 0),
          nozzle(v3(R * 0.97, legH + coneH + bodyH * 0.6, 0), v3(1, 0, 0), R * 0.13, 4),
        ),
        p(translate(ladder(h * 0.86), R + 3, 0, 0), 'galv'),
        p(translate(cylinder(1.4, h * 0.3, { seg: 8 }), R * 1.06, legH + coneH + bodyH * 0.35, 0), 'glass'),
      ],
      height: h + 2,
      ports: {
        N: { p: v3(0, h + 2, 0), n: v3(0, 1, 0) },
        S: { p: v3(0, legH * 0.25, 0), n: v3(0, -1, 0) },
        E: { p: v3(R * 1.02, legH + coneH + bodyH * 0.6, 0), n: v3(1, 0, 0) },
        W: { p: v3(-R, legH + coneH + bodyH * 0.6, 0), n: v3(-1, 0, 0) },
      },
      footprint: roundFoot(R * 1.06, 12),
    };
  };
}

export const rawmatsilo = feederSilo(0.26, 'steel');
export const granulessilo = feederSilo(0.24, 'steel');
export const powdersilo = feederSilo(0.3, 'stainless');
export const drytank = feederSilo(0.28, 'stainless');

/** Liquid feeder — a small dosing tank with a metering pump beneath. */
export const liquidfeeder: Builder = ({ w, h, d }): Model3 => {
  const R = Math.min(w, d) / 2.4;
  const baseH = h * 0.34;
  const tankH = h * 0.52;
  return {
    parts: [
      p(box(w * 0.72, baseH * 0.3, d * 0.72), 'galv'),
      p(translate(cylinder(R, tankH, { seg: 18 }), 0, baseH, 0), 'plastic'),
      p(translate(cylinder(R * 0.94, tankH * 0.66, { seg: 18 }), 0, baseH, 0), 'water'),
      pm(
        'stainless',
        translate(flange(R * 0.34, 2, 12), 0, baseH + tankH, 0),
        translate(cylinder(R * 0.14, baseH * 0.5, { seg: 8 }), 0, baseH * 0.5, 0),
      ),
      // Diaphragm dosing pump + its motor.
      p(translate(box(w * 0.3, baseH * 0.5, d * 0.42), -w * 0.24, baseH * 0.3, 0), 'cast'),
      p(translate(merge(motorUnit(w * 0.24, R * 0.26).body, motorUnit(w * 0.24, R * 0.26).fins), -w * 0.42, baseH * 0.55, 0), 'motor'),
      p(translate(cylinderX(R * 0.1, w * 0.5, 8), w * 0.1, baseH * 0.55, 0), 'pipe'),
    ],
    height: baseH + tankH + 2,
    ports: {
      N: { p: v3(0, baseH + tankH + 2, 0), n: v3(0, 1, 0) },
      E: { p: v3(w / 2, baseH * 0.55, 0), n: v3(1, 0, 0) },
      W: { p: v3(-w / 2, baseH * 0.55, 0), n: v3(-1, 0, 0) },
      S: { p: v3(0, baseH * 0.3, 0), n: v3(0, -1, 0) },
    },
    footprint: rectFoot(w, d),
  };
};

// ─── Pneumatic conveying ─────────────────────────────────────────────────

/** Cyclone — tangential inlet, long cone, vortex finder and dust outlet. */
export const cyclone: Builder = ({ w, h, d }): Model3 => {
  const R = Math.min(w, d) / 2;
  const legH = h * 0.14;
  const coneH = h * 0.46;
  const bodyH = h - legH - coneH;
  return {
    parts: [
      p(ringLegs(R * 1.02, legH + coneH * 0.55, 3, 1.7), 'galv'),
      p(
        revolve(
          [
            { x: R * 0.2, y: legH },
            { x: R, y: legH + coneH },
            { x: R, y: legH + coneH + bodyH },
          ],
          22,
          false,
        ),
        'stainless',
      ),
      pm(
        'stainless',
        // Flat lid + vortex finder standing proud of it.
        translate(flange(R, 1.6, 22), 0, legH + coneH + bodyH, 0),
        translate(cylinder(R * 0.34, bodyH * 0.5, { seg: 14 }), 0, legH + coneH + bodyH, 0),
        translate(flange(R * 0.44, 1.6, 14), 0, legH + coneH + bodyH + bodyH * 0.5, 0),
        // Tangential inlet on the barrel.
        translate(cylinderZ(R * 0.28, d * 0.7, 12), R * 0.62, legH + coneH + bodyH * 0.72, -d * 0.35),
        translate(cylinder(R * 0.24, legH * 0.7, { seg: 10 }), 0, legH * 0.3, 0),
      ),
    ],
    height: legH + coneH + bodyH + bodyH * 0.5 + 2,
    ports: {
      N: { p: v3(0, legH + coneH + bodyH + bodyH * 0.5 + 2, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, legH * 0.3, 0), n: v3(0, -1, 0) },
      E: { p: v3(R * 0.62, legH + coneH + bodyH * 0.72, -d * 0.36), n: v3(0, 0, -1) },
      W: { p: v3(-R, legH + coneH + bodyH * 0.72, 0), n: v3(-1, 0, 0) },
    },
    footprint: roundFoot(R * 1.1, 12),
  };
};

/** Filter receiver — cone-bottomed vessel with a filter head and pulse valves. */
export const filterreceiver: Builder = ({ w, h, d }): Model3 => {
  const R = Math.min(w, d) / 2;
  const legH = h * 0.12;
  const coneH = h * 0.28;
  const bodyH = h * 0.44;
  const headH = h * 0.16;
  const headY = legH + coneH + bodyH;
  return {
    parts: [
      p(ringLegs(R * 0.92, legH, 3, 1.7), 'galv'),
      p(
        revolve(
          [
            { x: R * 0.22, y: legH },
            { x: R, y: legH + coneH },
            { x: R, y: headY },
          ],
          22,
          false,
        ),
        'stainless',
      ),
      pm(
        'painted',
        translate(cylinder(R * 1.04, headH, { seg: 22 }), 0, headY, 0),
        translate(frustum(R * 1.04, R * 0.6, headH * 0.5, { seg: 22 }), 0, headY + headH, 0),
      ),
      // Pulse-jet air header and its solenoid valves.
      pm(
        'pipe',
        translate(cylinderZ(R * 0.16, d * 1.1, 10), R * 1.05, headY + headH * 0.5, -d * 0.55),
        translate(cylinder(R * 0.2, headH * 0.6, { seg: 10 }), 0, headY + headH * 1.5, 0),
      ),
      p(
        merge(
          translate(boxCentered(R * 0.3, R * 0.3, R * 0.3), R * 1.05, headY + headH * 0.8, -d * 0.22),
          translate(boxCentered(R * 0.3, R * 0.3, R * 0.3), R * 1.05, headY + headH * 0.8, d * 0.22),
        ),
        'accent',
      ),
      p(translate(cylinder(R * 0.22, legH * 0.6, { seg: 10 }), 0, legH * 0.25, 0), 'stainless'),
      p(nozzle(v3(-R * 0.98, legH + coneH + bodyH * 0.5, 0), v3(-1, 0, 0), R * 0.2, 5), 'stainless'),
    ],
    height: headY + headH * 1.5 + headH * 0.6,
    ports: {
      N: { p: v3(0, headY + headH * 2.1, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, legH * 0.25, 0), n: v3(0, -1, 0) },
      W: { p: v3(-R * 0.98 - 5, legH + coneH + bodyH * 0.5, 0), n: v3(-1, 0, 0) },
      E: { p: v3(R * 1.04, headY + headH * 0.5, 0), n: v3(1, 0, 0) },
    },
    footprint: roundFoot(R * 1.08, 12),
  };
};

/** Vacuum receiver / loader — a small hopper receiver with a filter cap. */
function receiver(tall: boolean): Builder {
  return ({ w, h, d }): Model3 => {
    const R = Math.min(w, d) / 2;
    const coneH = h * (tall ? 0.32 : 0.4);
    const bodyH = h * (tall ? 0.4 : 0.34);
    const capH = h * 0.2;
    const y0 = h * 0.06;
    return {
      parts: [
        p(ringLegs(R * 0.8, y0, 3, 1.4), 'galv'),
        p(
          revolve(
            [
              { x: R * 0.22, y: y0 },
              { x: R, y: y0 + coneH },
              { x: R, y: y0 + coneH + bodyH },
            ],
            20,
            false,
          ),
          'stainless',
        ),
        pm(
          'painted',
          translate(cylinder(R * 1.05, capH, { seg: 20 }), 0, y0 + coneH + bodyH, 0),
          translate(frustum(R * 1.05, R * 0.44, capH * 0.45, { seg: 20 }), 0, y0 + coneH + bodyH + capH, 0),
        ),
        p(translate(cylinder(R * 0.44, capH * 0.4, { seg: 12 }), 0, y0 + coneH + bodyH + capH * 1.45, 0), 'pipe'),
        p(translate(cylinderZ(R * 0.24, d * 0.7, 12), R * 0.5, y0 + coneH * 0.8, -d * 0.35), 'stainless'),
        p(translate(cylinder(R * 0.24, y0 * 0.8, { seg: 10 }), 0, y0 * 0.1, 0), 'stainless'),
        p(translate(box(R * 0.5, R * 0.4, 1.6), 0, y0 + coneH + bodyH * 0.4, R * 0.99), 'glass'),
      ],
      height: y0 + coneH + bodyH + capH * 1.85,
      ports: {
        N: { p: v3(0, y0 + coneH + bodyH + capH * 1.85, 0), n: v3(0, 1, 0) },
        S: { p: v3(0, y0 * 0.1, 0), n: v3(0, -1, 0) },
        E: { p: v3(R * 0.5, y0 + coneH * 0.8, -d * 0.36), n: v3(0, 0, -1) },
        W: { p: v3(-R, y0 + coneH + bodyH * 0.5, 0), n: v3(-1, 0, 0) },
      },
      footprint: roundFoot(R * 1.06, 12),
    };
  };
}

export const vacloader = receiver(true);
export const vacreceiver = receiver(false);

/** Blower — volute casing, inlet silencer, V-belt guard and motor on a skid. */
export const blower: Builder = ({ w, h, d }): Model3 => {
  const R = Math.min(h, d) / 2.6;
  const cy = h * 0.5;
  return {
    parts: [
      p(skid(w * 0.98, d * 0.98, h * 0.12), 'galv'),
      // Volute: a squat cylinder with a tangential discharge stub.
      p(
        merge(
          translate(cylinderZ(R, d * 0.5, 20), -w * 0.16, cy, -d * 0.25),
          translate(cylinderZ(R * 1.1, 2, 20), -w * 0.16, cy, d * 0.26),
        ),
        'cast',
      ),
      p(translate(cylinder(R * 0.4, R * 0.9, { seg: 12 }), -w * 0.16, cy + R * 0.8, 0), 'pipe'),
      // Inlet silencer.
      p(translate(cylinder(R * 0.62, R * 1.2, { seg: 14 }), -w * 0.16, cy + R * 1.5, 0), 'lagging'),
      p(translate(merge(motorUnit(w * 0.42, R * 0.5).body, motorUnit(w * 0.42, R * 0.5).fins), w * 0.24, cy * 0.8, 0), 'motor'),
      // Belt guard between the two.
      p(translate(box(w * 0.2, R * 1.6, 2), w * 0.02, cy * 0.4, d * 0.3), 'safety'),
      p(nozzle(v3(-w * 0.16, cy, -d * 0.26), v3(0, 0, -1), R * 0.3, 4), 'pipe'),
    ],
    height: cy + R * 2.7,
    ports: {
      N: { p: v3(-w * 0.16, cy + R * 2.7, 0), n: v3(0, 1, 0) },
      W: { p: v3(-w / 2, cy, 0), n: v3(-1, 0, 0) },
      E: { p: v3(w / 2, cy * 0.8, 0), n: v3(1, 0, 0) },
      S: { p: v3(-w * 0.16, cy, d * 0.3), n: v3(0, 0, 1) },
    },
    footprint: rectFoot(w, d),
  };
};

/** Roots blower — a squarer twin-lobe package with an acoustic hood. */
export const rootsblower: Builder = ({ w, h, d }): Model3 => {
  const cy = h * 0.46;
  const R = Math.min(h, d) / 3.4;
  return {
    parts: [
      p(skid(w * 0.98, d * 0.98, h * 0.14), 'galv'),
      p(translate(box(w * 0.42, h * 0.44, d * 0.66), -w * 0.2, h * 0.14, 0), 'cast'),
      p(
        merge(
          translate(cylinderZ(R, d * 0.68, 16), -w * 0.2, cy + R * 0.5, -d * 0.34),
          translate(cylinderZ(R, d * 0.68, 16), -w * 0.2, cy - R * 0.5, -d * 0.34),
        ),
        'cast',
      ),
      p(translate(merge(motorUnit(w * 0.4, R * 0.62).body, motorUnit(w * 0.4, R * 0.62).fins), w * 0.24, cy, 0), 'motor'),
      p(translate(box(w * 0.16, h * 0.5, 2), w * 0.02, h * 0.14, d * 0.34), 'safety'),
      pm(
        'pipe',
        translate(cylinder(R * 0.5, h * 0.2, { seg: 12 }), -w * 0.2, cy + R * 1.4, 0),
        translate(cylinder(R * 0.5, h * 0.16, { seg: 12 }), -w * 0.36, h * 0.14, 0),
      ),
    ],
    height: cy + R * 1.4 + h * 0.2,
    ports: {
      N: { p: v3(-w * 0.2, cy + R * 1.4 + h * 0.2, 0), n: v3(0, 1, 0) },
      W: { p: v3(-w / 2, cy, 0), n: v3(-1, 0, 0) },
      E: { p: v3(w / 2, cy, 0), n: v3(1, 0, 0) },
      S: { p: v3(-w * 0.36, h * 0.14, 0), n: v3(0, -1, 0) },
    },
    footprint: rectFoot(w, d),
  };
};

/** 2- and 3-way diverters — a pipe body with branches and a pneumatic actuator. */
function diverter(ways: 2 | 3): Builder {
  return ({ w, h, d }): Model3 => {
    const r = Math.min(w, d) * 0.17;
    const cy = h * 0.44;
    const branches: Mesh[] = [
      translate(cylinder(r, cy, { seg: 14 }), 0, 0, 0),
      translate(rotateZ(cylinder(r, w * 0.44, { seg: 14 }), -Math.PI / 3.2), 0, cy, 0),
    ];
    if (ways === 3) branches.push(translate(rotateZ(cylinder(r, w * 0.44, { seg: 14 }), Math.PI / 3.2), 0, cy, 0));
    else branches.push(translate(cylinder(r, h * 0.3, { seg: 14 }), 0, cy, 0));
    return {
      parts: [
        p(merge(...branches), 'stainless'),
        p(translate(frustum(r * 1.5, r * 1.5, r * 1.4, { seg: 16 }), 0, cy - r * 0.7, 0), 'cast'),
        pm(
          'accent',
          translate(cylinder(r * 0.8, h * 0.28, { seg: 12 }), 0, cy + r * 1.2, -d * 0.3),
          translate(flange(r, 1.4, 12), 0, cy + r * 1.2 + h * 0.28, -d * 0.3),
        ),
        pm(
          'stainless',
          translate(flange(r * 1.4, 1.4, 14), 0, 1, 0),
          translate(flange(r * 1.4, 1.4, 14), 0, h - 2, 0),
        ),
      ],
      height: h,
      ports: {
        S: { p: v3(0, 0, 0), n: v3(0, -1, 0) },
        N: { p: v3(0, h, 0), n: v3(0, 1, 0) },
        E: { p: v3(w * 0.36, cy + w * 0.22, 0), n: v3(0.7, 0.7, 0) },
        W: { p: v3(-w * 0.36, cy + w * 0.22, 0), n: v3(-0.7, 0.7, 0) },
      },
      footprint: rectFoot(w * 0.9, d * 0.7),
    };
  };
}

export const diverter2 = diverter(2);
export const diverter3 = diverter(3);
export const divertervalve = diverter(3);

/**
 * 90° elbow.
 *
 * The 2D symbol is drawn in PLAN: it turns a horizontal run through a right
 * angle, from an up-facing mouth on its top edge to a right-facing mouth on its
 * right edge (see `engine/shapes/ports.ts`). The 3D model mirrors those two
 * mouths exactly, so a connector docks onto the fitting instead of stopping
 * beside it.
 *
 * The bend itself is SWEPT with the same `tube` + `filletPath` pair the routed
 * pipework uses, rather than assembled from a torus and two stubs. That is the
 * whole reason it now matches the runs it joins: one piece, one continuous
 * surface, one set of smooth normals, no seam where the arc meets the tangent.
 */
export const elbow90: Builder = ({ w, h }): Model3 => {
  const D = Math.min(w, h);
  const r = D * 0.2;
  // Straight face length — the same fraction `elbowStub` uses in 2D.
  const t = D * 0.18;
  const standH = D * 0.5;
  const cy = standH + r * 1.35;

  // Mouth positions, mirroring the 2D port anchors in local coordinates.
  const ax = t - w / 2;
  const bz = h / 2 - t;
  const mouthN = v3(ax, cy, -h / 2);
  const mouthE = v3(w / 2, cy, bz);
  const corner = v3(ax, cy, bz);
  // Keep the fillet inside both legs, or the bend eats its own flange faces.
  const bendR = Math.min(D * 0.4, (bz + h / 2) * 0.45, (w / 2 - ax) * 0.45);

  const body = tube(filletPath([mouthN, corner, mouthE], bendR, 6), r, 16);

  /** A raised-face flange with its bolt circle, square to `dir`. */
  const flangeAt = (at: typeof mouthN, dir: 'z' | 'x') => {
    const face = dir === 'z' ? cylinderZ(r * 1.72, 2.6, 16) : cylinderX(r * 1.72, 2.6, 16);
    const hub = dir === 'z' ? cylinderZ(r * 1.28, 5.5, 16) : cylinderX(r * 1.28, 5.5, 16);
    const bolts: Mesh[] = [];
    for (let i = 0; i < 6; i++) {
      const a = (TAU * i) / 6 + Math.PI / 6;
      const u = Math.cos(a) * r * 1.42;
      const v = Math.sin(a) * r * 1.42;
      const b = dir === 'z' ? cylinderZ(r * 0.16, 3.6, 6) : cylinderX(r * 0.16, 3.6, 6);
      bolts.push(translate(b, dir === 'z' ? u : 0, v, dir === 'z' ? 0 : u));
    }
    const g = merge(face, hub, ...bolts);
    return translate(g, at.x, at.y, at.z);
  };

  return {
    parts: [
      p(body, 'pipe'),
      pm('machine', flangeAt(mouthN, 'z'), flangeAt(mouthE, 'x')),
      // A saddle on a short leg — an elbow is a fitting, it does not need a
      // platform built round it, but it does have to stand on something.
      pm(
        'galv',
        translate(cylinder(r * 0.42, standH, { seg: 8 }), corner.x, 0, corner.z),
        translate(box(r * 2.6, 2.2, r * 2.6), corner.x, standH, corner.z),
      ),
    ],
    height: cy + r * 1.8,
    // N and W both resolve to the up-facing mouth, E and S to the right-facing
    // one — the same aliasing the 2D symbol uses, so a connector can never dock
    // to a face the fitting doesn't have.
    ports: {
      N: { p: mouthN, n: v3(0, 0, -1) },
      W: { p: mouthN, n: v3(0, 0, -1) },
      E: { p: mouthE, n: v3(1, 0, 0) },
      S: { p: mouthE, n: v3(1, 0, 0) },
    },
    footprint: rectFoot(w * 0.55, h * 0.55),
  };
};

/** Connecting tube — a plain flanged spool piece. */
export const connectingtube: Builder = ({ w, h, d }): Model3 => {
  const r = Math.min(w, d) * 0.28;
  return {
    parts: [
      p(cylinder(r, h, { seg: 16 }), 'pipe'),
      pm('machine', flange(r * 1.5, 1.8, 16), translate(flange(r * 1.5, 1.8, 16), 0, h - 1.8, 0)),
    ],
    height: h,
    ports: {
      S: { p: v3(0, 0, 0), n: v3(0, -1, 0) },
      N: { p: v3(0, h, 0), n: v3(0, 1, 0) },
      E: { p: v3(r, h / 2, 0), n: v3(1, 0, 0) },
      W: { p: v3(-r, h / 2, 0), n: v3(-1, 0, 0) },
    },
    footprint: roundFoot(r * 1.5, 10),
  };
};

/** Fluidized bed dryer — a tall column with a plenum and a filter head. */
export const fluidizedbed: Builder = ({ w, h, d }): Model3 => {
  const R = Math.min(w, d) / 2;
  const legH = h * 0.14;
  const plenumH = h * 0.2;
  const colH = h * 0.44;
  const headH = h * 0.22;
  return {
    parts: [
      p(ringLegs(R * 0.86, legH, 4, 1.8), 'galv'),
      p(translate(frustum(R * 0.7, R, plenumH, { seg: 20 }), 0, legH, 0), 'painted'),
      p(translate(cylinder(R, colH, { seg: 20, capTop: false, capBottom: false }), 0, legH + plenumH, 0), 'stainless'),
      p(translate(box(R * 0.7, colH * 0.6, 1.4), 0, legH + plenumH + colH * 0.2, R * 0.99), 'glass'),
      p(translate(frustum(R, R * 1.14, headH, { seg: 20 }), 0, legH + plenumH + colH, 0), 'painted'),
      pm(
        'pipe',
        translate(cylinder(R * 0.26, headH * 0.6, { seg: 12 }), 0, legH + plenumH + colH + headH, 0),
        translate(cylinderZ(R * 0.24, d * 0.6, 12), -R * 0.4, legH + plenumH * 0.5, -d * 0.3),
      ),
      p(nozzle(v3(R * 0.98, legH + plenumH + colH * 0.7, 0), v3(1, 0, 0), R * 0.16, 4), 'stainless'),
    ],
    height: legH + plenumH + colH + headH + headH * 0.6,
    ports: {
      N: { p: v3(0, legH + plenumH + colH + headH * 1.6, 0), n: v3(0, 1, 0) },
      S: { p: v3(-R * 0.4, legH + plenumH * 0.5, -d * 0.31), n: v3(0, 0, -1) },
      E: { p: v3(R * 1.04, legH + plenumH + colH * 0.7, 0), n: v3(1, 0, 0) },
      W: { p: v3(-R, legH + plenumH + colH * 0.4, 0), n: v3(-1, 0, 0) },
    },
    footprint: roundFoot(R * 1.1, 12),
  };
};
